import * as React from "react"
import { cn } from "@/lib/utils"

/**
 * A single-line field.
 *
 * ── Fill at rest, ring on focus ──────────────────────────────────────────────────────────
 *
 * It was a square block of `--secondary` with a rule underneath that thickened from 1px to 2px
 * on focus. Two things were wrong with that. It was the only square, borderless surface in an
 * app whose every other object is rounded — cards at `2xl`, menus at `4xl`, buttons and badges
 * at `full` — and it grew HEAVIER as it became active, which is backwards: the field you are
 * typing into should come forward, not thicken a line under itself.
 *
 * So: the resting state is the fill, and focus takes the fill away and draws a ring. The ring is
 * `border-foreground` plus `ring-1`, which is exactly `ItemCard`'s selected state — so a focused
 * field and a selected card say the same thing the same way, rather than each inventing its own.
 *
 * `rounded-full`, because a single line of text in a pill is the shape this app already uses for
 * everything else that holds one line. `Textarea` is `rounded-2xl` instead: a pill on a
 * multi-line box curves the first and last lines out of alignment with the middle ones.
 *
 * ── Invalid ──────────────────────────────────────────────────────────────────────────────
 *
 * The ring turns red instead of the rule. `has-[[aria-invalid=true]]` reads the attribute
 * FormControl already sets on the input, so a field reports itself without the caller doing
 * anything. This replaced a red label and a line of red text under every field — stock shadcn
 * error styling, and the one thing on those pages that looked like a browser's own validation
 * rather than like this app. The message goes to a toast; this is only "which field".
 */
function Input({ className, type, ...props }: React.ComponentProps<"input">) {
  return (
    <div
      className={cn(
        // A visible edge at rest, not a transparent one.
        //
        // The fill alone does not define the field everywhere it is used: `--card` and
        // `--secondary` are the SAME value in the dark palette, so a secondary-filled field on a
        // card — the sign-in, unlock and account panels, all of them — was a field you could not
        // see. The old underline hid this, because a rule is an edge even when the fill matches
        // its ground. `--border` is distinct from both in both themes, so the field defines
        // itself wherever it is put, and focus then only has to change the edge's colour.
        "group w-full rounded-full border border-border bg-secondary px-5",
        "transition-[background-color,border-color,box-shadow] duration-200",
        // `:not(:focus-within)` written out rather than left to variant order: `hover:` and
        // `focus-within:` have equal specificity, so which one wins while a focused field is
        // hovered would otherwise depend on the order Tailwind happens to emit them in.
        "[&:hover:not(:focus-within)]:bg-secondary",
        "focus-within:border-foreground focus-within:bg-secondary focus-within:ring-1 focus-within:ring-foreground",
        "has-[[aria-invalid=true]]:border-destructive has-[[aria-invalid=true]]:ring-1 has-[[aria-invalid=true]]:ring-destructive",
        "has-[:disabled]:pointer-events-none has-[:disabled]:opacity-50",
        className,
      )}
    >
      <input
        type={type}
        data-slot="input"
        className={cn(
          "h-12 w-full min-w-0 bg-transparent text-sm text-foreground",
          "placeholder:text-muted-foreground/50 outline-none",
          "file:inline-flex file:h-7 file:border-0 file:bg-transparent",
          "file:text-sm file:font-medium file:text-foreground",
          "disabled:cursor-not-allowed",
          "aria-invalid:text-destructive",
        )}
        {...props}
      />
    </div>
  )
}

export { Input }
